import { useEffect, useRef, useState } from "react";

/**
 * Tracks whether the user has requested reduced motion, reacting to changes.
 * @returns True when prefers-reduced-motion is set to reduce.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(query.matches);
    const onChange = () => setReduced(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

/**
 * Smoothly animates a number toward a target on each change (ease-out cubic),
 * counting from the previously displayed value. Snaps instantly when the user
 * prefers reduced motion.
 * @param target - The value to animate toward.
 * @param durationMs - The animation duration in milliseconds.
 * @returns The current animated value.
 */
export function useCountUp(target: number, durationMs = 750): number {
  const [value, setValue] = useState(0);
  const fromRef = useRef(0);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (reduced) {
      fromRef.current = target;
      setValue(target);
      return;
    }
    const from = fromRef.current;
    let raf = 0;
    let startTime = 0;
    const tick = (now: number) => {
      startTime = startTime || now;
      const progress = Math.min(1, (now - startTime) / durationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(from + (target - from) * eased);
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
        return;
      }
      fromRef.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, reduced]);

  return value;
}
